const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;
uniform float u_speed;
uniform float u_intensity;
uniform float u_detail;
uniform float u_rotation;
uniform bool u_invert;
uniform float u_seed;

mat2 rot2d(float angle) {
    return mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
}

float r(float a, float b) {
    return fract(sin(dot(vec2(a, b), vec2(12.9898, 78.233))) * 43758.5453);
}

float h(float a) {
    return fract(sin(dot(a, dot(12.9898, 78.233))) * 43758.5453);
}

float noise(vec3 x) {
    vec3 p = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    float n = p.x + p.y * 57.0 + 113.0 * p.z;
    return mix(mix(mix(h(n + 0.0), h(n + 1.0), f.x),
                   mix(h(n + 57.0), h(n + 58.0), f.x), f.y),
               mix(mix(h(n + 113.0), h(n + 114.0), f.x),
                   mix(h(n + 170.0), h(n + 171.0), f.x), f.y), f.z);
}

vec3 dnoise2f(vec2 p) {
    float i = floor(p.x), j = floor(p.y);
    float u = p.x - i, v = p.y - j;
    float du = 30. * u * u * (u * (u - 2.) + 1.);
    float dv = 30. * v * v * (v * (v - 2.) + 1.);
    u = u * u * u * (u * (u * 6. - 15.) + 10.);
    v = v * v * v * (v * (v * 6. - 15.) + 10.);
    float a = r(i, j);
    float b = r(i + 1.0, j);
    float c = r(i, j + 1.0);
    float d = r(i + 1.0, j + 1.0);
    float k0 = a;
    float k1 = b - a;
    float k2 = c - a;
    float k3 = a - b - c + d;
    return vec3(k0 + k1 * u + k2 * v + k3 * u * v,
                du * (k1 + k3 * v),
                dv * (k2 + k3 * u));
}

float fbm(vec2 uv) {
    vec2 p = uv;
    float f, dx, dz, w = 0.5;
    f = dx = dz = 0.0;
    int iterations = int(u_detail * 28.0);
    for (int i = 0; i < 28; ++i) {
        if (i >= iterations) break;
        vec3 n = dnoise2f(uv);
        dx += n.y;
        dz += n.z;
        f += w * n.x / (1.0 + dx * dx + dz * dz);
        w *= 0.86;
        uv *= vec2(1.16);
        uv *= rot2d(1.25 * noise(vec3(p * 0.1 + u_seed * 0.1, 0.12 * u_time * u_speed)) +
                    0.75 * noise(vec3(p * 0.1 + u_seed * 0.2, 0.20 * u_time * u_speed)));
    }
    return f;
}

float fbmLow(vec2 uv) {
    float f, dx, dz, w = 0.5;
    f = dx = dz = 0.0;
    for (int i = 0; i < 4; ++i) {
        vec3 n = dnoise2f(uv);
        dx += n.y;
        dz += n.z;
        f += w * n.x / (1.0 + dx * dx + dz * dz);
        w *= 0.75;
        uv *= vec2(1.5);
    }
    return f;
}

vec3 paletteColor(float t) {
    t = fract(t);
    if (t < 0.2) return mix(u_color_0, u_color_1, t * 5.0);
    else if (t < 0.4) return mix(u_color_1, u_color_2, (t - 0.2) * 5.0);
    else if (t < 0.6) return mix(u_color_2, u_color_3, (t - 0.4) * 5.0);
    else if (t < 0.8) return mix(u_color_3, u_color_4, (t - 0.6) * 5.0);
    else return mix(u_color_4, u_color_5, (t - 0.8) * 5.0);
}

void main() {
    vec2 uv = 1.0 - 2.0 * (gl_FragCoord.xy / u_resolution.xy);
    uv.y /= u_resolution.x / u_resolution.y;
    float t = u_time * u_speed * 0.6;
    
    vec2 rv = uv / (length(uv * 2.5) * (uv * 30.0));
    uv *= rot2d(0.3 * t * u_rotation + u_seed);
    float val = 0.5 * fbm(uv * 2.0 * fbmLow(length(uv) + rv - t));
    uv *= rot2d(-0.6 * t * u_rotation);
    
    float spillValue = 0.5 * fbm(uv * val * 8.0) + 0.02 * r(uv.x, uv.y);
    
    vec3 color;
    if (u_invert) {
        color = 1.0 - 1.2 * vec3(spillValue);
    } else {
        color = 1.6 * vec3(spillValue);
    }
    
    // Apply color palette based on the spill pattern
    vec3 paletteCol = paletteColor(spillValue * 3.0 + u_seed * 0.1);
    color = mix(color, paletteCol, 0.7);
    
    color *= u_intensity;
    color = color / (1.0 + color);
    color = smoothstep(0.18, 0.88, color);
    
    gl_FragColor = vec4(color, 1.0);
}
`;

const shader = {
  name: 'TurbulentSpill',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#1a1a2e', '#16213e', '#0f4c75', '#3282b8', '#bbe1fa', '#ffffff'],
    },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 5, step: 0.1, default: 1 },
    {
      key: 'u_intensity',
      type: 'number',
      label: 'Intensity',
      min: 1,
      max: 8,
      step: 0.1,
      default: 3.8,
    },
    {
      key: 'u_detail',
      type: 'number',
      label: 'Detail',
      min: 0.1,
      max: 1,
      step: 0.05,
      default: 0.8,
    },
    {
      key: 'u_rotation',
      type: 'number',
      label: 'Rotation',
      min: 0,
      max: 2,
      step: 0.1,
      default: 1,
    },
    { key: 'u_invert', type: 'boolean', label: 'Invert', default: false },
    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(TurbulentSpill, colors=#1a1a2e,#16213e,#0f4c75,#3282b8,#bbe1fa,#fff; u_speed=1; u_intensity=3.8; u_detail=0.8; animated=true)',
    'dynamic-shader(TurbulentSpill, colors=#ff6b6b,#ee5a52,#ff7675,#fd79a8,#fdcb6e,#6c5ce7; u_speed=0.5; u_intensity=4.2; u_invert=true; animated=true)',
    'dynamic-shader(TurbulentSpill, colors=#2d3436,#636e72,#74b9ff,#0984e3,#00b894,#00cec9; u_speed=1.5; u_detail=0.6; u_rotation=1.5; animated=true)',
    'dynamic-shader(TurbulentSpill, colors=#000,#333,#666,#999,#ccc,#fff; u_speed=0.8; u_intensity=2.5; u_detail=1.0; u_invert=false; animated=false)',
  ],
};

export default shader;
